import { useEffect, useState } from "react";
import axios from "axios";
import { TICKETS, VENUE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Checkout() {
  const [selected, setSelected] = useState("vip");
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [buyer, setBuyer] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const ticket = TICKETS.find((t) => t.id === selected);
  const remaining = availability[selected]?.remaining;
  const maxQty = Math.min(10, remaining ?? 10);

  useEffect(() => {
    axios
      .get(`${API}/payments/availability`)
      .then(({ data }) => setAvailability(data))
      .catch(() => {});
  }, []);

  const select = (t) => {
    if (availability[t.id]?.remaining === 0) return;
    setSelected(t.id);
    setQty(1);
    setError("");
  };

  const proceed = async () => {
    if (remaining === 0) return;
    if (remaining !== undefined && qty > remaining) {
      setError(`Only ${remaining} seat(s) remaining for ${ticket.name}.`);
      return;
    }
    if (!buyer.first_name.trim() || !buyer.last_name.trim()) {
      setError("Please add your first and last name.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(buyer.email.trim())) {
      setError("Please add a valid email address — your briefing confirmation goes there.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: selected,
        quantity: qty,
        origin_url: window.location.origin,
        first_name: buyer.first_name,
        last_name: buyer.last_name,
        email: buyer.email,
        phone: buyer.phone,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Secure checkout could not be opened. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <header className="border-b border-ink/15">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-10">
          <a href="/" data-testid="checkout-brand-link" className="flex items-center gap-3">
            <img
              src="/assets/wmwe-logo-dark.png"
              alt="WMWE logo"
              className="h-9 w-9 rounded-full border border-ink/15 bg-cream object-contain p-[3px]"
            />
            <span className="font-sans text-[11px] tracking-[0.35em] text-ink">
              RECON 2026 <span className="text-ink/40">// SECURE CHECKOUT</span>
            </span>
          </a>
          <a
            href="/"
            data-testid="checkout-return-link"
            className="font-sans text-[10px] tracking-[0.3em] text-ink/50 underline underline-offset-4 transition-colors duration-200 hover:text-wine"
          >
            ← BACK TO BRIEFING
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 md:px-10 md:py-24">
        <p
          data-testid="checkout-label"
          className="font-sans text-[11px] uppercase tracking-[0.4em] text-wine"
        >
          Ticketing // Your Official Briefing
        </p>
        <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Secure your <span className="italic text-wine">seat.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-smoke">
          November 13–14, 2026 // {VENUE.name}, {VENUE.address}, {VENUE.city}. Choose your
          experience — every seat includes the 2026 RECON Field Guide.
        </p>

        <div
          data-testid="ticket-options"
          className="mt-14 grid items-start gap-px border border-ink/20 bg-ink/20 md:grid-cols-3"
        >
          {TICKETS.map((t) => {
            const soldOut = availability[t.id]?.remaining === 0;
            const left = availability[t.id]?.remaining;
            const isOpen = expanded === t.id;
            return (
              <div
                key={t.id}
                role="button"
                tabIndex={0}
                data-testid={`ticket-option-${t.id}`}
                onClick={() => select(t)}
                onKeyDown={(e) => e.key === "Enter" && select(t)}
                className={`relative p-8 text-left transition-colors duration-300 ${
                  soldOut
                    ? "cursor-not-allowed bg-bone opacity-70"
                    : selected === t.id
                      ? "cursor-pointer bg-manila"
                      : "cursor-pointer bg-bone hover:bg-[#FBEFD0]"
                }`}
              >
                {t.best && !soldOut && (
                  <span className="absolute right-6 top-6 bg-wine px-2.5 py-1 font-sans text-[8px] tracking-[0.25em] text-gold">
                    BOTH DAYS — BEST VALUE
                  </span>
                )}
                {soldOut && (
                  <span
                    data-testid={`soldout-stamp-${t.id}`}
                    className="absolute right-6 top-6 -rotate-6 border-[3px] border-wine px-3 py-1.5 font-sans text-[10px] font-semibold tracking-[0.3em] text-wine"
                  >
                    SOLD OUT
                  </span>
                )}
                <p className="font-sans text-[10px] tracking-[0.3em] text-ink/40">TICKET {t.file}</p>
                <h2 className="mt-5 font-serif text-3xl font-medium tracking-tight text-ink">
                  {t.name}
                </h2>
                <p className="mt-2 font-sans text-[10px] leading-relaxed tracking-[0.2em] text-ink/60">
                  {t.day.toUpperCase()}
                  <br />
                  {t.time.toUpperCase()}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-smoke">{t.lead}</p>

                <p className="mt-5 font-sans text-[9px] tracking-[0.3em] text-oxblood">
                  YOUR TICKET INCLUDES
                </p>
                <ul className="mt-2 space-y-1.5">
                  {t.includes.map((item) => (
                    <li
                      key={item}
                      className="flex items-baseline gap-2.5 font-sans text-[10px] tracking-[0.12em] text-ink/75"
                    >
                      <span className="text-gold">+</span>
                      {item}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  data-testid={`read-more-${t.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(isOpen ? null : t.id);
                  }}
                  className="mt-4 font-sans text-[9px] tracking-[0.3em] text-wine underline underline-offset-4"
                >
                  {isOpen ? "CLOSE FULL DESCRIPTION ↑" : "READ FULL DESCRIPTION ↓"}
                </button>
                {isOpen && (
                  <div
                    data-testid={`full-copy-${t.id}`}
                    className="mt-4 space-y-3 border-t border-ink/15 pt-4"
                  >
                    {t.full.map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-smoke">
                        {para}
                      </p>
                    ))}
                  </div>
                )}

                <p className="mt-6 font-serif text-4xl font-medium text-wine">${t.price}</p>
                <p
                  data-testid={`remaining-${t.id}`}
                  className={`mt-2 font-sans text-[9px] tracking-[0.25em] ${soldOut ? "text-wine" : "text-ink/60"}`}
                >
                  {left === undefined
                    ? `${t.capacity} SEATS`
                    : soldOut
                      ? "NO SEATS REMAINING"
                      : `${left} OF ${t.capacity} SEATS REMAINING`}
                </p>
                {!soldOut && (
                  <span
                    className={`mt-5 inline-block border px-4 py-1.5 font-sans text-[9px] tracking-[0.3em] transition-colors duration-300 ${
                      selected === t.id
                        ? "border-wine bg-wine text-bone"
                        : "border-ink/30 text-ink/60"
                    }`}
                  >
                    {selected === t.id ? "SELECTED" : "SELECT"}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col gap-8 border border-ink/20 bg-manila p-8 md:flex-row md:items-center md:justify-between md:p-10">
          <div>
            <p className="font-sans text-[10px] tracking-[0.3em] text-ink/50">SEATS</p>
            <div className="mt-3 flex items-center gap-5">
              <button
                type="button"
                data-testid="qty-minus-button"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="flex h-10 w-10 items-center justify-center border border-ink/30 font-sans text-lg transition-colors duration-200 hover:border-wine hover:text-wine"
              >
                −
              </button>
              <span data-testid="qty-display" className="font-serif text-3xl font-medium tabular-nums text-ink">
                {qty}
              </span>
              <button
                type="button"
                data-testid="qty-plus-button"
                onClick={() => setQty(Math.min(maxQty, qty + 1))}
                className="flex h-10 w-10 items-center justify-center border border-ink/30 font-sans text-lg transition-colors duration-200 hover:border-wine hover:text-wine"
              >
                +
              </button>
            </div>
            {remaining !== undefined && remaining <= 5 && remaining > 0 && (
              <p data-testid="low-seats-warning" className="mt-3 font-sans text-[9px] tracking-[0.25em] text-wine">
                ONLY {remaining} REMAINING
              </p>
            )}
          </div>
          <div className="md:text-right">
            <p className="font-sans text-[10px] tracking-[0.3em] text-ink/50">TOTAL DUE</p>
            <p data-testid="order-total" className="mt-2 font-serif text-4xl font-medium text-ink">
              ${(ticket.price * qty).toLocaleString()}
            </p>
            <p className="mt-1 font-sans text-[9px] tracking-[0.2em] text-ink/45">
              {qty} × {ticket.name.toUpperCase()}
            </p>
          </div>
        </div>

        <div className="mt-10 border border-ink/20 bg-bone p-8 md:p-10" data-testid="buyer-details">
          <p className="font-sans text-[10px] tracking-[0.3em] text-ink/50">
            YOUR DETAILS // WHERE WE SEND YOUR BRIEFING
          </p>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            <div>
              <label className="block font-sans text-[9px] tracking-[0.25em] text-ink/45">
                FIRST NAME *
              </label>
              <input
                type="text"
                value={buyer.first_name}
                onChange={(e) => setBuyer({ ...buyer, first_name: e.target.value })}
                data-testid="buyer-first-name"
                className="mt-2 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />
            </div>
            <div>
              <label className="block font-sans text-[9px] tracking-[0.25em] text-ink/45">
                LAST NAME *
              </label>
              <input
                type="text"
                value={buyer.last_name}
                onChange={(e) => setBuyer({ ...buyer, last_name: e.target.value })}
                data-testid="buyer-last-name"
                className="mt-2 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />
            </div>
            <div>
              <label className="block font-sans text-[9px] tracking-[0.25em] text-ink/45">
                EMAIL *
              </label>
              <input
                type="email"
                value={buyer.email}
                onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                data-testid="buyer-email"
                className="mt-2 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />
            </div>
            <div>
              <label className="block font-sans text-[9px] tracking-[0.25em] text-ink/45">
                PHONE (OPTIONAL)
              </label>
              <input
                type="tel"
                value={buyer.phone}
                onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                data-testid="buyer-phone"
                className="mt-2 w-full border border-ink/30 bg-transparent px-4 py-3 text-sm text-ink outline-none transition-colors duration-200 focus:border-wine"
              />
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={proceed}
          disabled={loading}
          data-testid="proceed-checkout-button"
          className="group relative mt-10 inline-flex w-full items-center justify-center overflow-hidden border border-wine bg-wine px-10 py-6 font-sans text-xs tracking-[0.4em] text-bone disabled:opacity-60 md:w-auto"
        >
          <span className="absolute inset-0 translate-y-full bg-navy transition-transform duration-500 ease-out group-hover:translate-y-0" />
          <span className="relative">
            {loading ? "OPENING SECURE CHECKOUT..." : "PROCEED TO SECURE CHECKOUT"}
          </span>
          {!loading && (
            <span className="relative ml-4 inline-block transition-transform duration-300 group-hover:translate-x-1.5">
              →
            </span>
          )}
        </button>
        {error && (
          <p data-testid="checkout-error" className="mt-4 font-sans text-[10px] tracking-[0.2em] text-wine">
            {error}
          </p>
        )}
        <p className="mt-6 font-sans text-[9px] tracking-[0.25em] text-ink/45">
          PAYMENTS PROCESSED SECURELY BY STRIPE // CARDS, APPLE PAY + GOOGLE PAY ACCEPTED
        </p>
      </main>
    </div>
  );
}
